/**
 * Console log capture for debugging and issue reporting
 *
 * Intercepts console.log, console.warn, console.error and stores them
 * in a buffer that can be copied for GitHub issues.
 */
const MAX_LOG_ENTRIES = 500;
const logBuffer = [];
// Store original console methods
const originalConsole = {
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
};
function formatArgs(args) {
    return args.map(arg => {
        if (arg instanceof Error) {
            return `${arg.message}\n${arg.stack}`;
        }
        if (typeof arg === 'object') {
            try {
                return JSON.stringify(arg, null, 2);
            }
            catch {
                return String(arg);
            }
        }
        return String(arg);
    }).join(' ');
}
function addLogEntry(level, args) {
    const entry = {
        timestamp: new Date().toISOString(),
        level,
        message: formatArgs(args),
    };
    logBuffer.push(entry);
    // Trim buffer if too large
    if (logBuffer.length > MAX_LOG_ENTRIES) {
        logBuffer.splice(0, logBuffer.length - MAX_LOG_ENTRIES);
    }
}
/**
 * Initialize log capture by patching console methods
 */
export function initLogCapture() {
    console.log = (...args) => {
        addLogEntry('log', args);
        originalConsole.log(...args);
    };
    console.warn = (...args) => {
        addLogEntry('warn', args);
        originalConsole.warn(...args);
    };
    console.error = (...args) => {
        addLogEntry('error', args);
        originalConsole.error(...args);
    };
    // Capture unhandled errors
    window.addEventListener('error', (event) => {
        addLogEntry('error', [`Unhandled error: ${event.message}`, event.filename, `line ${event.lineno}`]);
    });
    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        addLogEntry('error', [`Unhandled promise rejection: ${event.reason}`]);
    });
}
/**
 * Get all captured log entries
 */
export function getLogEntries() {
    return [...logBuffer];
}
/**
 * Get only error and warning entries
 */
export function getErrorsAndWarnings() {
    return logBuffer.filter(entry => entry.level === 'error' || entry.level === 'warn');
}
/**
 * Clear the log buffer
 */
export function clearLogs() {
    logBuffer.length = 0;
}
/**
 * Get error count
 */
export function getErrorCount() {
    return logBuffer.filter(entry => entry.level === 'error').length;
}
/**
 * Get warning count
 */
export function getWarningCount() {
    return logBuffer.filter(entry => entry.level === 'warn').length;
}
/**
 * Format environment info for GitHub issue (without logs for privacy)
 */
export function formatEnvironmentForGitHub(options) {
    const { buildTime, gitCommit, gitBranch, editorName, editorDescription } = options;
    const userAgent = navigator.userAgent;
    const url = window.location.href;
    return `## Environment

| Property | Value |
|----------|-------|
| Editor | ${editorName} - ${editorDescription} |
| Build | \`${gitCommit}\` |
| Branch | \`${gitBranch}\` |
| Build Time | ${buildTime} |
| URL | ${url} |
| User Agent | ${userAgent} |

## Description

<!-- Please describe the issue you encountered -->

## Steps to Reproduce

<!-- Please list the steps to reproduce the issue -->

1.
2.
3.

## Expected Behavior

<!-- What did you expect to happen? -->

## Actual Behavior

<!-- What actually happened? -->

## Console Logs

<!-- If relevant, paste console logs here (use "Copy for Issue" button to include logs) -->
`;
}
/**
 * Format logs for GitHub issue (includes logs - use for manual copy)
 */
export function formatLogsForGitHub(options) {
    const { includeAllLogs = false, ...envOptions } = options;
    const logs = includeAllLogs ? getLogEntries() : getErrorsAndWarnings();
    const recentLogs = logs.slice(-100); // Last 100 entries
    let output = formatEnvironmentForGitHub(envOptions);
    // Replace the placeholder comment with actual logs
    if (recentLogs.length > 0) {
        output = output.replace('<!-- If relevant, paste console logs here (use "Copy for Issue" button to include logs) -->', `\`\`\`
${recentLogs.map(entry => {
            const levelIcon = entry.level === 'error' ? '[ERROR]' : entry.level === 'warn' ? '[WARN]' : '[LOG]';
            return `${entry.timestamp} ${levelIcon} ${entry.message}`;
        }).join('\n')}
\`\`\``);
    }
    else {
        output = output.replace('<!-- If relevant, paste console logs here (use "Copy for Issue" button to include logs) -->', 'No errors or warnings captured.');
    }
    return output;
}
//# sourceMappingURL=logCapture.js.map